import React, { useState, useEffect } from "react";
import { AxiosResponse } from "axios";
import Spinner from "components/Spinner";
import PayslipsPaginatedTable from "features/payslips/PayslipsPaginatedTable";
import { PayslipListResponse } from "types/payroll-types";
import IconButton from "components/IconButton";
import { fetchAllPayslipsAPI, fetchPayslipsPageAPI, deletePayslipAPI} from "api/apiHelper";
import ErrorBox from "components/ErrorBox";
import { usePermissions } from "app/hooks";
import DownloadReportsModal from "features/payslips/DownloadReportsModal";
import { toast } from "react-toastify";
import DeleteModal from "components/DeleteModal";


const Payslips = () => {
  const [payslips, setPayslips] = useState<PayslipListResponse>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [pageLimit, setPageLimit] = useState<number>(20);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const permissions = usePermissions() || [];
  const [openDownloadReportsModal, setOpenDownloadReportsModal] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedPayslipId, setSelectedPayslipId] = useState<number | null>(null);
  
  const openDeleteModal = (id: number) => {
    setSelectedPayslipId(id);
    setShowDeleteModal(true);
  };
  
  const closeDeleteModal = () => {
    setSelectedPayslipId(null);
    setShowDeleteModal(false);
  };

  const handleSearch = async () => {
    setIsLoading(true);
    try {
      const response: AxiosResponse<PayslipListResponse> = await fetchAllPayslipsAPI(pageLimit, searchTerm);
      setPayslips(response.data);
      setPageNumber(1);
      setIsLoading(false);
    } catch (error: any) {
      console.log(error);
      setError(error.message);
      setIsLoading(false);
    }
  };
  const handleDeletePayslip = async () => {
    if (selectedPayslipId === null) return;
  
    const notification = toast.loading("Deleting payslip...");
    try {
      // Call delete API here
      await deletePayslipAPI(selectedPayslipId);
      // await deletePayslipAPI(selectedPayslipId);
      toast.update(notification, {
        render: "Payslip deleted successfully",
        type: "success",
        isLoading: false,
        autoClose: 5000,
      });
      closeDeleteModal();
      getPayslips();
    } catch (error: any) {
      console.log(error);
      
      let errorMessage = "Failed to delete payslip";
      if (error?.response?.data?.detail) {
        errorMessage = error.response.data.detail;
        if (errorMessage.length > 80) {
          errorMessage = "Failed to delete payslip";
        }
      }
      toast.update(notification, {
        render: errorMessage,
        type: "error",
        isLoading: false,
        autoClose: 5000,
      });
    }
  };

  async function getPayslips() {
    try {
      const response: AxiosResponse<PayslipListResponse> = await fetchAllPayslipsAPI(pageLimit);
      console.log("Payslips : ", response.data);
      setPayslips(response.data);
      setPageNumber(1);
      setIsLoading(false);
    }
    catch (error: any) {
      console.log(error);
      setError(error.message);
      setIsLoading(false);
    }
  }

  const getPayslipPage = async (pageUrl: string, action: string) => {
    setIsLoading(true);
    try {
      // Append search to paginated URL if applicable
      const urlWithSearch = searchTerm ? `${pageUrl}&search=${encodeURIComponent(searchTerm)}` : pageUrl;
      const response: AxiosResponse<PayslipListResponse> = await fetchPayslipsPageAPI(urlWithSearch);
      setPayslips(response.data);
      setPageNumber((prev) => (action === "next" ? prev + 1 : prev - 1));
      setIsLoading(false);
    } catch (error: any) {
      console.log(error);
      setError(error.message);
      setIsLoading(false);
    }
  };
  
  async function getPayslipPageWithLimit(pageSize:number) {
    setIsLoading(true);
    try {
      const response: AxiosResponse<PayslipListResponse> = await fetchAllPayslipsAPI(pageSize);
      console.log("Payslips Page Size Res: ", response.data);
      setPageLimit(pageSize);
      setPayslips(response.data);
      setPageNumber(1);
      setIsLoading(false);
    }
    catch (error: any) {
      console.log(error);
      setError(error.message);
      setIsLoading(false);
    }
  }

  useEffect(() => {
    getPayslips();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  return (
    <div className="mt-5">
      <div className="flex md:flex-row md:justify-between items-center mb-4">
        <div className="flex flex-wrap justify-start gap-2">
          {permissions && permissions.includes('payroll.view_payslipbatch') &&
            <IconButton url="/payslip/batch" name="Batches" icon="fas fa-tasks" extraStyles="mb-2 md:mb-0" />
          }
          {permissions && (permissions.includes('payroll.view_paysliplineitemtype') || permissions.includes('payroll.add_paysliplineitemtype')) &&
            <IconButton url="/payslip/line-item-types" name="Item Types" icon="fas fa-tasks" extraStyles="mb-2 md:mb-0" />
          }
        </div>
        <div className="flex flex-wrap justify-end md:justify-end gap-2">
          {permissions && permissions.includes('payroll.add_payslipbatch') &&
            <IconButton url="/payslip/batch/create" name="Create Batch" icon="fas fa-plus" extraStyles="mb-2 md:mb-0" />
          }
          {permissions && permissions.includes('payroll.add_payslip') &&
            <IconButton url="/payslip/create" name="Create" icon="fas fa-plus" extraStyles="mb-2 md:mb-0" />
          }
          {permissions && permissions.includes('payroll.view_payslipbatch') &&
            <IconButton onClick={() => setOpenDownloadReportsModal(true)} name="Download Reports" icon="fas fa-download" extraStyles="mb-2 md:mb-0 !bg-white !text-gray-700" />
          }
          <DownloadReportsModal open={openDownloadReportsModal} onClose={() => setOpenDownloadReportsModal(false)} />        </div>
      </div>
      {isLoading ? (
          <Spinner show={isLoading} />        
        )
          : (
          <>
            {error && <ErrorBox errorMessage={error} />}
            {payslips && <PayslipsPaginatedTable tableData={payslips} tableName="Payslips" pageLimit={pageLimit} currentPageNumber={pageNumber} getPayslipPage={getPayslipPage} getPayslipPageWithLimit={getPayslipPageWithLimit} getPayslips={getPayslips} handleSearch={handleSearch}  searchTerm={searchTerm} setSearchTerm={setSearchTerm} handleDeletePayslip={handleDeletePayslip} openDeleteModal={openDeleteModal} closeDeleteModal={closeDeleteModal} selectedPayslipId={selectedPayslipId} />}
            {showDeleteModal && (
            <DeleteModal
              onClose={closeDeleteModal}
              onConfirm={handleDeletePayslip}
              message="Are you sure you want to delete this payslip?"
            />
            )}
          </>
        )
      }
    </div>
  );
};

export default Payslips;